// components/analytics/sections/UserAnalytics.tsx
import React, { useState, useEffect } from "react";
import { Row, Col, Statistic, Typography, Tooltip, Button, Modal, InputNumber, Space } from "antd";
import {
  UserOutlined,
  DollarOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  RiseOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  CrownOutlined,
  EyeOutlined,
  SettingOutlined,
  CloseOutlined
} from "@ant-design/icons";
import { TrendLineChart } from "../components/charts/TrendLineChart";
import { TopTierChart } from "../components/charts/TopTierChart";
import { useAnalytics } from "../../../contexts/AnalyticsContext";
import { useTheme } from "../../../contexts/ThemeContext";
import type { UserAnalytics as UserAnalyticsType, AnalyticsFilters } from "../../../types/analytics";
import { useTranslation } from "react-i18next";
import { UserDetailsDrawer } from "../components/UserDetailsDrawer";
import { StreakUsersDrawer } from "../components/StreakUsersDrawer";
import { AnalyticsService } from "../../../services/analyticsService";

const { Text } = Typography;

interface UserAnalyticsProps {
  data?: UserAnalyticsType | null;
  loading?: boolean;
}

// Componente StatCard rediseñado con glassmorphism
const ModernStatCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  suffix?: string;
  prefix?: string | React.ReactNode;
  precision?: number;
  loading?: boolean;
  info?: string;
  theme?: 'dark' | 'light';
}> = ({ title, value, icon, color, suffix, prefix, precision, loading, info, theme = 'light' }) => (
  <div
    style={{
      background: theme === 'dark' 
        ? '#23272f' 
        : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16,
      padding: '20px',
      boxShadow: theme === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.32)'
        : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      backdropFilter: 'blur(12px)',
      border: theme === 'dark'
        ? '1px solid #23272f'
        : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
    }}
  >
    {/* Gradiente decorativo */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '60px',
        height: '60px',
        background: theme === 'dark'
          ? `linear-gradient(135deg, ${color}33, #23272f00)`
          : `linear-gradient(135deg, ${color}20, ${color}10)` ,
        borderRadius: '0 16px 0 60px'
      }}
    />
    
    {/* Header con icono */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <Text style={{ 
        fontSize: '13px', 
        fontWeight: 600, 
        color: theme === 'dark' ? '#d9d9d9' : '#595959', 
        margin: 0 
      }}>
        {title}
      </Text>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px' 
      }}>
        {info && (
          <Tooltip title={info}>
            <InfoCircleOutlined style={{ 
              color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf', 
              fontSize: '12px' 
            }} />
          </Tooltip>
        )}
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${color}, ${color}dd)` ,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '14px',
            boxShadow: `0 2px 8px ${color}40`
          }}
        >
          {icon}
        </div>
      </div>
    </div>

    {/* Valor principal */}
    <div style={{ zIndex: 1 }}>
      <Statistic
        value={value}
        prefix={prefix}
        suffix={suffix}
        precision={precision}
        loading={loading}
        valueStyle={{ 
          fontSize: '24px', 
          fontWeight: 700, 
          color: theme === 'dark' ? '#fff' : '#262626',
          margin: 0
        }}
      />
    </div>
  </div>
);

// Componente para cards con información adicional
// Componente para cards clickeables con configuración
const ClickableStatCard: React.FC<{
  title: string;
  value: number | string;
  info: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  theme?: 'dark' | 'light';
  onClick?: () => void;
  onConfig?: () => void;
}> = ({ title, value, info, icon, color, loading, theme = 'light', onClick, onConfig }) => (
  <div
    onClick={onClick}
    style={{
      background: theme === 'dark' ? '#23272f' : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16,
      padding: '20px',
      boxShadow: theme === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.32)'
        : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      backdropFilter: 'blur(12px)',
      border: theme === 'dark'
        ? '1px solid #23272f'
        : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.3s ease',
      transform: 'translateY(0)',
    }}
    onMouseEnter={(e) => {
      if (onClick) {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = theme === 'dark'
          ? `0 4px 16px ${color}40`
          : `0 8px 24px ${color}30`;
      }
    }}
    onMouseLeave={(e) => {
      if (onClick) {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = theme === 'dark'
          ? '0 2px 8px rgba(0,0,0,0.32)'
          : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)';
      }
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '4px',
        background: `linear-gradient(90deg, ${color}, ${color}80, transparent)`
      }}
    />

    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <Text style={{
        fontSize: '13px',
        fontWeight: 600,
        color: theme === 'dark' ? '#d9d9d9' : '#595959',
        margin: 0,
        flex: 1
      }}>
        {title}
      </Text>
      <Space size={8}>
        {onConfig && (
          <Button
            type="text"
            size="small"
            icon={<SettingOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onConfig();
            }}
            style={{
              color: color,
              padding: '4px 8px',
              height: 'auto',
              zIndex: 10,
              opacity: 0.8,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.8';
            }}
          />
        )}
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${color}, ${color}dd)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '14px',
            boxShadow: `0 2px 8px ${color}40`
          }}
        >
          {icon}
        </div>
      </Space>
    </div>

    <div style={{ zIndex: 1 }}>
      <Statistic
        value={value}
        loading={loading}
        valueStyle={{
          fontSize: '20px',
          fontWeight: 700,
          color: theme === 'dark' ? '#fff' : '#262626',
          margin: 0
        }}
      />
      <Text style={{
        fontSize: '11px',
        color: theme === 'dark' ? '#bfbfbf' : '#8c8c8c',
        marginTop: '4px',
        display: 'block'
      }}>
        {info}
        {onClick && (
          <span style={{
            marginLeft: '8px',
            color: color,
            fontWeight: 600
          }}>
            • Click para ver detalles
          </span>
        )}
      </Text>
    </div>
  </div>
);

const InfoStatCard: React.FC<{
  title: string;
  value: number | string;
  info: string;
  icon: React.ReactNode;
  color: string;
  prefix?: string | React.ReactNode;
  precision?: number;
  loading?: boolean;
  theme?: 'dark' | 'light';
}> = ({ title, value, info, icon, color, prefix, precision, loading, theme = 'light' }) => (
  <div
    style={{
      background: theme === 'dark' 
        ? '#23272f' 
        : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16,
      padding: '20px',
      boxShadow: theme === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.32)'
        : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      backdropFilter: 'blur(12px)',
      border: theme === 'dark'
        ? '1px solid #23272f'
        : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
    }}
  >
    {/* Gradiente decorativo */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '4px',
        background: theme === 'dark'
          ? `linear-gradient(90deg, ${color}, #23272f00)`
          : `linear-gradient(90deg, ${color}, ${color}80, transparent)`
      }}
    />
    
    {/* Header con icono */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <Text style={{ 
        fontSize: '13px', 
        fontWeight: 600, 
        color: theme === 'dark' ? '#d9d9d9' : '#595959', 
        margin: 0 
      }}>
        {title}
      </Text>
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: `linear-gradient(135deg, ${color}, ${color}dd)` ,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '14px',
          boxShadow: `0 2px 8px ${color}40`
        }}
      >
        {icon}
      </div>
    </div>

    {/* Valor principal */}
    <div style={{ zIndex: 1 }}>
      <Statistic
        value={value}
        prefix={prefix}
        precision={precision}
        loading={loading}
        valueStyle={{ 
          fontSize: '20px', 
          fontWeight: 700, 
          color: theme === 'dark' ? '#fff' : '#262626',
          margin: 0
        }}
      />
      <Text style={{ 
        fontSize: '11px', 
        color: theme === 'dark' ? '#bfbfbf' : '#8c8c8c', 
        marginTop: '4px', 
        display: 'block' 
      }}>
        {info}
      </Text>
    </div>
  </div>
);

// Componente expandible para cards con detalles de usuarios
const ExpandableInfoStatCard: React.FC<{
  title: string;
  value: number | string;
  info: string;
  icon: React.ReactNode;
  color: string;
  prefix?: string | React.ReactNode;
  precision?: number;
  loading?: boolean;
  theme?: 'dark' | 'light';
  category?: 'usersWithDeposits' | 'whales' | 'dolphins' | 'fish' | 'inactive';
  categoryData?: {
    count: number;
    totalValue?: number;
    percentage: number;
  };
  onExpand?: (category: string) => void;
  expandable?: boolean;
}> = ({ 
  title, 
  value, 
  info, 
  icon, 
  color, 
  prefix, 
  precision, 
  loading, 
  theme = 'light',
  category,
  onExpand,
  expandable = true
}) => (
  <div
    style={{
      background: theme === 'dark' 
        ? '#23272f' 
        : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16,
      padding: '20px',
      boxShadow: theme === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.32)'
        : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      backdropFilter: 'blur(12px)',
      border: theme === 'dark'
        ? '1px solid #23272f'
        : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'all 0.3s ease',
      cursor: expandable ? 'pointer' : 'default',
      ...(expandable && {
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme === 'dark'
            ? '0 6px 16px rgba(0,0,0,0.4)'
            : '0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08)'
        }
      })
    }}
    onClick={expandable && category && onExpand ? () => onExpand(category) : undefined}
  >
    {/* Gradiente decorativo */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '4px',
        background: theme === 'dark'
          ? `linear-gradient(90deg, ${color}, #23272f00)`
          : `linear-gradient(90deg, ${color}, ${color}80, transparent)`
      }}
    />
    
    {/* Header con icono y botón de expansión */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <Text style={{ 
        fontSize: '13px', 
        fontWeight: 600, 
        color: theme === 'dark' ? '#d9d9d9' : '#595959', 
        margin: 0 
      }}>
        {title}
      </Text>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Botón de expansión */}
        {expandable && (
          <Tooltip title="Ver detalles de usuarios">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              style={{
                color: color,
                fontSize: '12px',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                background: `${color}10`,
                border: `1px solid ${color}30`
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (category && onExpand) {
                  onExpand(category);
                }
              }}
            />
          </Tooltip>
        )}
        {/* Icono principal */}
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${color}, ${color}dd)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '14px',
            boxShadow: `0 2px 8px ${color}40`
          }}
        >
          {icon}
        </div>
      </div>
    </div>

    {/* Valor principal */}
    <div style={{ zIndex: 1 }}>
      <Statistic
        value={value}
        prefix={prefix}
        precision={precision}
        loading={loading}
        valueStyle={{ 
          fontSize: '20px', 
          fontWeight: 700, 
          color: theme === 'dark' ? '#fff' : '#262626',
          margin: 0
        }}
      />
      <Text style={{ 
        fontSize: '11px', 
        color: theme === 'dark' ? '#bfbfbf' : '#8c8c8c', 
        marginTop: '4px', 
        display: 'block' 
      }}>
        {info}
        {expandable && (
          <span style={{ 
            marginLeft: '8px', 
            color: color, 
            fontWeight: 600 
          }}>
            • Expandir
          </span>
        )}
      </Text>
    </div>
  </div>
);

export const UserAnalytics: React.FC<UserAnalyticsProps> = ({
  data = {
    total: 0,
    byAccount: {},
    averageLoadAmount: 0,
    averageLoadsPerUser: 0,
    usersCreatedInRange: 0,
    usersWithoutCode: 0,
    averageLoadsPerUserCreatedInRange: 0,
    averageLoadAmountRangeUsers: 0,
    highestLoadRangeUsers: { amount: 0, username: "" },
    segmentsActivityInRange: {
      whales: { loadedInRangeCount: 0, totalLoadsInRange: 0, totalAmountInRange: 0, avgLoadsInRange: 0 },
      dolphins: { loadedInRangeCount: 0, totalLoadsInRange: 0, totalAmountInRange: 0, avgLoadsInRange: 0 },
      fish: { loadedInRangeCount: 0, totalLoadsInRange: 0, totalAmountInRange: 0, avgLoadsInRange: 0 },
      inactive: { loadedInRangeCount: 0, totalLoadsInRange: 0, totalAmountInRange: 0, avgLoadsInRange: 0 }
    },
    highestLoad: { amount: 0, username: "" },
    mostActiveDay: { day: "", count: 0 },
    mostActiveWeek: { week: 0, count: 0 },
    mostActiveHour: { hour: 0, count: 0 },
    trends: [],
    topLoaders: [],
    topWithdrawers: [],
    topLoadAmounts: [],
    topWithdrawAmounts: [],
    usersWithDeposits: { count: 0, percentage: 0 },
    userDepositDetails: [],
    // Nuevos KPIs con valores por defecto
    segmentation: {
      whales: { count: 0, totalValue: 0, percentage: 0 },
      dolphins: { count: 0, totalValue: 0, percentage: 0 },
      fish: { count: 0, totalValue: 0, percentage: 0 },
      inactive: { count: 0, totalValue: 0, percentage: 0 }
    },
    timeToFirstDeposit: {
      averageHours: 0,
      within24Hours: { count: 0, percentage: 0 },
      moreThan24Hours: { count: 0, percentage: 0 },
      conversationByDay: []
    },
    streakAnalysis: {
      longestStreak: { username: '', days: 0 },
      averageStreak: 0,
      superActiveUsers: { count: 0, percentage: 0, threshold: 0, details: [] },
      averageDaysBetweenFirstLast: 0,
      returningUsers: { count: 0, percentage: 0, inactivityDays: 0, details: [] }
    },
    depositWithdrawRatio: [],
    progressiveAmounts: {
      growthPattern: { count: 0, percentage: 0 },
      declinePattern: { count: 0, percentage: 0 },
      volatilePattern: { count: 0, percentage: 0 },
      stablePattern: { count: 0, percentage: 0 }
    },
    diversityAnalysis: {
      depositOnly: { count: 0, percentage: 0 },
      withdrawOnly: { count: 0, percentage: 0 },
      mixed: { count: 0, percentage: 0 },
      averageOperationTypes: 0,
      monoFunction: { count: 0, percentage: 0 },
      multiFunction: { count: 0, percentage: 0 }
    },
    personalTimePatterns: {
      nocturnalUsers: { count: 0, percentage: 0 },
      diurnalUsers: { count: 0, percentage: 0 },
      averageTimeConsistency: 0,
      topPersonalHours: []
    }
  },
  loading = false,
}) => {
  const { filters, setFilters } = useAnalytics();
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Estado para el drawer de detalles de usuarios
  const [drawerState, setDrawerState] = useState<{
    visible: boolean;
    category: 'total' | 'usersWithDeposits' | 'whales' | 'dolphins' | 'fish' | 'inactive' | null;
    categoryData?: {
      count: number;
      totalValue?: number;
      percentage: number;
    };
  }>({
    visible: false,
    category: null,
    categoryData: undefined
  });

  // Estado para el drawer de streak (super activos / que regresan)
  const [streakDrawerState, setStreakDrawerState] = useState<{
    visible: boolean;
    type: 'superActive' | 'returning' | null;
  }>({
    visible: false,
    type: null
  });

  // Estado para thresholds configurables
  const [customThresholds, setCustomThresholds] = useState<{
    superActive: number | null;
    returning: number | null;
  }>({
    superActive: null,
    returning: null
  });

  // Estado para modal de configuración
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [configType, setConfigType] = useState<'superActive' | 'returning' | null>(null);
  const [tempThreshold, setTempThreshold] = useState<number>(0);

  // Debug: Log cuando cambia el estado del modal
  useEffect(() => {
  }, [configModalVisible, configType, tempThreshold]);

  // Función para abrir el drawer de detalles
  const handleExpandCategory = (category: string) => {
    let categoryData;
    
    switch (category) {
      case 'total':
        categoryData = {
          count: filters.accountName
            ? data?.byAccount[filters.accountName] || 0
            : data?.total || 0,
          percentage: 100
        };
        break;
      case 'usersWithDeposits':
        categoryData = {
          count: data?.usersWithDeposits?.count || 0,
          percentage: data?.usersWithDeposits?.percentage || 0
        };
        break;
      case 'whales':
        categoryData = {
          count: data?.segmentation?.whales?.count || 0,
          totalValue: data?.segmentation?.whales?.totalValue || 0,
          percentage: data?.segmentation?.whales?.percentage || 0
        };
        break;
      case 'dolphins':
        categoryData = {
          count: data?.segmentation?.dolphins?.count || 0,
          totalValue: data?.segmentation?.dolphins?.totalValue || 0,
          percentage: data?.segmentation?.dolphins?.percentage || 0
        };
        break;
      case 'fish':
        categoryData = {
          count: data?.segmentation?.fish?.count || 0,
          totalValue: data?.segmentation?.fish?.totalValue || 0,
          percentage: data?.segmentation?.fish?.percentage || 0
        };
        break;
      case 'inactive':
        categoryData = {
          count: data?.segmentation?.inactive?.count || 0,
          totalValue: data?.segmentation?.inactive?.totalValue || 0,
          percentage: data?.segmentation?.inactive?.percentage || 0
        };
        break;
      default:
        return;
    }

    setDrawerState({
      visible: true,
      category: category as any,
      categoryData
    });
  };

  // Función para cerrar el drawer
  const handleCloseDrawer = () => {
    setDrawerState({
      visible: false,
      category: null,
      categoryData: undefined
    });
  };

  // Función para cargar detalles de usuarios
  const handleLoadUserDetails = async (category: string, limit: number, offset: number) => {
    try {
      return await AnalyticsService.getUserCategoryDetails(category, filters, limit, offset);
    } catch (error) {
      console.error('Error loading user details:', error);
      throw error;
    }
  };

  // Funciones para streak drawer
  const handleOpenStreakDrawer = (type: 'superActive' | 'returning') => {
    setStreakDrawerState({ visible: true, type });
  };

  const handleCloseStreakDrawer = () => {
    setStreakDrawerState({ visible: false, type: null });
  };

  // Funciones para configurar thresholds
  const handleOpenConfigModal = (type: 'superActive' | 'returning', currentValue: number) => {
    setConfigType(type);
    setTempThreshold(currentValue);
    setConfigModalVisible(true);
  };

  const handleApplyThreshold = () => {
    if (configType) {
      const newThresholds = {
        ...customThresholds,
        [configType]: tempThreshold
      };

      setCustomThresholds(newThresholds);
      setConfigModalVisible(false);

      // IMPORTANTE: Mantener TODOS los filtros existentes, solo actualizar thresholds
      const newFilters: AnalyticsFilters = {
        dateRange: filters.dateRange, // MANTENER el rango de fechas actual
        accountName: filters.accountName, // MANTENER cuenta actual
        token: filters.token, // MANTENER token
        adminId: filters.adminId, // MANTENER adminId
        platform: filters.platform,
        type: filters.type,
        status: filters.status,
        superActiveThreshold: configType === 'superActive' ? tempThreshold : (newThresholds.superActive || undefined),
        returningUserInactivityDays: configType === 'returning' ? tempThreshold : (newThresholds.returning || undefined),
        timestamp: Date.now() // Forzar recarga
      };


      setFilters(newFilters);
    }
  };

  const handleResetThreshold = () => {
    if (configType) {
      const newThresholds = {
        ...customThresholds,
        [configType]: null
      };

      setCustomThresholds(newThresholds);
      setConfigModalVisible(false);

      // IMPORTANTE: Mantener TODOS los filtros existentes, solo resetear thresholds
      const newFilters: AnalyticsFilters = {
        dateRange: filters.dateRange, // MANTENER el rango de fechas actual
        accountName: filters.accountName, // MANTENER cuenta actual
        token: filters.token, // MANTENER token
        adminId: filters.adminId, // MANTENER adminId
        platform: filters.platform,
        type: filters.type,
        status: filters.status,
        superActiveThreshold: configType === 'superActive' ? undefined : (newThresholds.superActive || undefined),
        returningUserInactivityDays: configType === 'returning' ? undefined : (newThresholds.returning || undefined),
        timestamp: Date.now() // Forzar recarga
      };


      setFilters(newFilters);
    }
  };

  return (
    <div style={{ padding: '8px 0' }}>
      {/* Métricas principales */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <div
            style={{ cursor: 'pointer' }}
            onClick={() => handleExpandCategory('total')}
          >
            <ModernStatCard
              title={t("analytics.userAnalytics.cards.totalUsers")}
              value={
                filters.accountName
                  ? data?.byAccount[filters.accountName] || 0
                  : data?.total || 0
              }
              icon={<UserOutlined />}
              color="#1890ff"
              loading={loading}
              theme={theme}
              info="Click para ver detalles de usuarios"
            />
          </div>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title={t("analytics.userAnalytics.cards.averageLoadAmount")}
            value={data?.averageLoadAmount || 0}
            info={`$${(data?.averageLoadAmountRangeUsers || 0).toFixed(2)} promedio usuarios en el rango`}
            icon={<DollarOutlined />}
            color="#52c41a"
            prefix="$"
            precision={2}
            loading={loading}
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title={t("analytics.userAnalytics.cards.averageLoadsPerUser")}
            value={data?.averageLoadsPerUser || 0}
            info={`${(data?.averageLoadsPerUserCreatedInRange || 0).toFixed(2)} cargas promedio usuarios en el rango`}
            icon={<RiseOutlined />}
            color="#722ed1"
            precision={2}
            loading={loading}
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title={t("analytics.userAnalytics.cards.highestLoad.title")}
            value={data?.highestLoad?.amount || 0}
            info={`$${(data?.highestLoadRangeUsers?.amount || 0).toFixed(2)} máxima usuarios en el rango`}
            icon={<TrophyOutlined />}
            color="#faad14"
            prefix="$"
            precision={2}
            loading={loading}
            theme={theme}
          />
        </Col>
      </Row>

      {/* Métricas temporales */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title={t("analytics.userAnalytics.cards.mostActiveDay")}
            value={
              data?.mostActiveDay?.day
                ? t(
                    "analytics.userAnalytics.days." +
                      data?.mostActiveDay?.day.toLocaleLowerCase()
                  )
                : t("analytics.userAnalytics.labels.notAvailable")
            }
            info={`${data?.mostActiveDay?.count || 0} ${t(
              "analytics.userAnalytics.labels.users"
            )}`}
            icon={<CalendarOutlined />}
            color="#13c2c2"
            loading={loading}
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title={t("analytics.userAnalytics.cards.mostActiveHour")}
            value={
              data?.mostActiveHour?.hour !== undefined
                ? `${data.mostActiveHour.hour}:00`
                : t("analytics.userAnalytics.labels.notAvailable")
            }
            info={`${data?.mostActiveHour?.count || 0} ${t(
              "analytics.userAnalytics.labels.users"
            )}`}
            icon={<ClockCircleOutlined />}
            color="#eb2f96"
            loading={loading}
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title={t("analytics.userAnalytics.cards.peakWeek.title")}
            value={`${t("analytics.userAnalytics.cards.peakWeek.week")} ${
              data?.mostActiveWeek?.week || 0
            }`}
            info={`${data?.mostActiveWeek?.count || 0} ${t(
              "analytics.userAnalytics.labels.users"
            )}`}
            icon={<CalendarOutlined />}
            color="#fa8c16"
            loading={loading}
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ExpandableInfoStatCard
            title={t("analytics.userAnalytics.cards.usersWithDeposits.title") || "Users with Deposits"}
            value={`${(data?.usersWithDeposits?.percentage || 0).toFixed(2)}%`}
            info={`${data?.usersWithDeposits?.count || 0} de ${data?.total || 0} usuarios`}
            icon={<TeamOutlined />}
            color="#f5222d"
            loading={loading}
            theme={theme}
            category="usersWithDeposits"
            categoryData={{
              count: data?.usersWithDeposits?.count || 0,
              percentage: data?.usersWithDeposits?.percentage || 0
            }}
            onExpand={handleExpandCategory}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title="👤 Usuarios sin Código"
            value={data?.usersWithoutCode || 0}
            info={`${((data?.usersWithoutCode || 0) / Math.max(data?.total || 1, 1) * 100).toFixed(1)}% del total`}
            icon={<UserOutlined />}
            color="#faad14"
            loading={loading}
            theme={theme}
          />
        </Col>
      </Row>

      {/* ==================== NUEVOS KPIs AVANZADOS ==================== */}
      
      {/* Segmentación por Valor de Usuario */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Text style={{ 
            fontSize: '18px', 
            fontWeight: 600, 
            marginBottom: 16, 
            display: 'block' 
          }}>
            📊 Segmentación por Valor de Usuario
          </Text>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <ExpandableInfoStatCard
            title="🐋 Ballenas (>$500k)"
            value={data?.segmentation?.whales?.count || 0}
            info={`${data?.segmentsActivityInRange?.whales?.loadedInRangeCount || 0} cargaron en el rango (${(data?.segmentsActivityInRange?.whales?.avgLoadsInRange || 0).toFixed(1)} promedio)`}
            icon={<CrownOutlined />}
            color="#722ed1"
            loading={loading}
            theme={theme}
            category="whales"
            categoryData={{
              count: data?.segmentation?.whales?.count || 0,
              totalValue: data?.segmentation?.whales?.totalValue || 0,
              percentage: data?.segmentation?.whales?.percentage || 0
            }}
            onExpand={handleExpandCategory}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ExpandableInfoStatCard
            title="🐬 Delfines ($100k-$500k)"
            value={data?.segmentation?.dolphins?.count || 0}
            info={`${data?.segmentsActivityInRange?.dolphins?.loadedInRangeCount || 0} cargaron en el rango (${(data?.segmentsActivityInRange?.dolphins?.avgLoadsInRange || 0).toFixed(1)} promedio)`}
            icon={<TrophyOutlined />}
            color="#1890ff"
            loading={loading}
            theme={theme}
            category="dolphins"
            categoryData={{
              count: data?.segmentation?.dolphins?.count || 0,
              totalValue: data?.segmentation?.dolphins?.totalValue || 0,
              percentage: data?.segmentation?.dolphins?.percentage || 0
            }}
            onExpand={handleExpandCategory}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ExpandableInfoStatCard
            title="🐟 Peces ($10k-$100k)"
            value={data?.segmentation?.fish?.count || 0}
            info={`${data?.segmentsActivityInRange?.fish?.loadedInRangeCount || 0} cargaron en el rango (${(data?.segmentsActivityInRange?.fish?.avgLoadsInRange || 0).toFixed(1)} promedio)`}
            icon={<RiseOutlined />}
            color="#52c41a"
            loading={loading}
            theme={theme}
            category="fish"
            categoryData={{
              count: data?.segmentation?.fish?.count || 0,
              totalValue: data?.segmentation?.fish?.totalValue || 0,
              percentage: data?.segmentation?.fish?.percentage || 0
            }}
            onExpand={handleExpandCategory}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ExpandableInfoStatCard
            title="💤 Inactivos (≤$10k)"
            value={data?.segmentation?.inactive?.count || 0}
            info={`${data?.segmentsActivityInRange?.inactive?.loadedInRangeCount || 0} cargaron en el rango (${(data?.segmentsActivityInRange?.inactive?.avgLoadsInRange || 0).toFixed(1)} promedio)`}
            icon={<UserOutlined />}
            color="#bfbfbf"
            loading={loading}
            theme={theme}
            category="inactive"
            categoryData={{
              count: data?.segmentation?.inactive?.count || 0,
              totalValue: data?.segmentation?.inactive?.totalValue || 0,
              percentage: data?.segmentation?.inactive?.percentage || 0
            }}
            onExpand={handleExpandCategory}
          />
        </Col>
      </Row>

      {/* Velocidad de Primera Carga y Análisis de Rachas */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Text style={{ 
            fontSize: '18px', 
            fontWeight: 600, 
            marginBottom: 16, 
            display: 'block' 
          }}>
            ⚡ Velocidad y Frecuencia
          </Text>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title="⏱️ Conversión a 1ra Carga"
            value={`${data?.timeToFirstDeposit?.totalUsersWhoDeposited || 0} de ${data?.timeToFirstDeposit?.totalUsersCreated || 0}`}
            info={`${(data?.timeToFirstDeposit?.conversionRate || 0).toFixed(1)}% - Promedio: ${(data?.timeToFirstDeposit?.averageHours || 0).toFixed(1)}h`}
            icon={<ClockCircleOutlined />}
            color="#fa8c16"
            loading={loading}
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title="🔥 Racha Más Larga"
            value={`${data?.streakAnalysis?.longestStreak?.days || 0} días`}
            info={`Por ${data?.streakAnalysis?.longestStreak?.username || 'N/A'}`}
            icon={<TrophyOutlined />}
            color="#f5222d"
            loading={loading}
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ClickableStatCard
            title={`⭐ Súper Activos (${customThresholds.superActive || data?.streakAnalysis?.superActiveUsers?.threshold || 0}+ cargas)`}
            value={data?.streakAnalysis?.superActiveUsers?.count || 0}
            info={`${(data?.streakAnalysis?.superActiveUsers?.percentage || 0).toFixed(1)}% del total`}
            icon={<RiseOutlined />}
            color="#eb2f96"
            loading={loading}
            theme={theme}
            onClick={() => handleOpenStreakDrawer('superActive')}
            onConfig={() => handleOpenConfigModal('superActive', data?.streakAnalysis?.superActiveUsers?.threshold || 5)}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ClickableStatCard
            title="🔄 Usuarios que Regresan"
            value={data?.streakAnalysis?.returningUsers?.count || 0}
            info={`${(data?.streakAnalysis?.returningUsers?.percentage || 0).toFixed(1)}% después de ${customThresholds.returning || data?.streakAnalysis?.returningUsers?.inactivityDays || 0}+ días`}
            icon={<TeamOutlined />}
            color="#13c2c2"
            loading={loading}
            theme={theme}
            onClick={() => handleOpenStreakDrawer('returning')}
            onConfig={() => handleOpenConfigModal('returning', data?.streakAnalysis?.returningUsers?.inactivityDays || 3)}
          />
        </Col>
      </Row>

      {/* Análisis de Comportamiento */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Text style={{ 
            fontSize: '18px', 
            fontWeight: 600, 
            color: theme === 'dark' ? '#ffffff' : '#262626', 
            marginBottom: 16, 
            display: 'block' 
          }}>
            🎯 Análisis de Comportamiento
          </Text>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title="📈 Patrones de Crecimiento"
            value={data?.progressiveAmounts?.growthPattern?.count || 0}
            info={`${(data?.progressiveAmounts?.growthPattern?.percentage || 0).toFixed(1)}% incrementan montos`}
            icon={<RiseOutlined />}
            color="#52c41a"
            loading={loading}
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title="🔄 Solo Depósitos"
            value={data?.diversityAnalysis?.depositOnly?.count || 0}
            info={`${(data?.diversityAnalysis?.depositOnly?.percentage || 0).toFixed(1)}% mono-función`}
            icon={<DollarOutlined />}
            color="#1890ff"
            loading={loading}
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title="🌙 Usuarios Nocturnos"
            value={data?.personalTimePatterns?.nocturnalUsers?.count || 0}
            info={`${(data?.personalTimePatterns?.nocturnalUsers?.percentage || 0).toFixed(1)}% activos 22:00-06:00`}
            icon={<ClockCircleOutlined />}
            color="#722ed1"
            loading={loading}
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title="🎲 Patrones Volátiles"
            value={data?.progressiveAmounts?.volatilePattern?.count || 0}
            info={`${(data?.progressiveAmounts?.volatilePattern?.percentage || 0).toFixed(1)}% montos erráticos`}
            icon={<RiseOutlined />}
            color="#fa8c16"
            loading={loading}
            theme={theme}
          />
        </Col>
      </Row>

      {/* Ratio Depósito/Retiro Top Users */}
      {data?.depositWithdrawRatio && data.depositWithdrawRatio.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col xs={24}>
            <div
              style={{
                background: theme === 'dark' 
                  ? '#23272f' 
                  : 'rgba(255, 255, 255, 0.95)',
                borderRadius: 16,
                padding: '20px',
                boxShadow: theme === 'dark'
                  ? '0 2px 8px rgba(0,0,0,0.32)'
                  : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
                backdropFilter: 'blur(12px)',
                border: theme === 'dark'
                  ? '1px solid #23272f'
                  : '1px solid rgba(0, 0, 0, 0.06)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
              }}
            >
              {/* Header del gráfico */}
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #52c41a, #389e0d)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                >
                  💰
                </div>
                <Text style={{ 
                  fontSize: '16px', 
                  fontWeight: 600, 
                  color: theme === 'dark' ? '#ffffff' : '#262626', 
                  margin: 0 
                }}>
                  💰 Top Ratios Depósito/Retiro
                </Text>
              </div>
              
              <div style={{ display: 'grid', gap: '12px' }}>
                {data.depositWithdrawRatio.slice(0, 5).map((user, index) => (
                  <div 
                    key={user.username}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: theme === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '8px',
                      border: theme === 'dark'
                        ? '1px solid rgba(255, 255, 255, 0.1)'
                        : '1px solid rgba(0, 0, 0, 0.04)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: user.profitability === 'high' ? '#52c41a' : 
                                     user.profitability === 'medium' ? '#faad14' : '#f5222d',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: '10px',
                          fontWeight: 600
                        }}
                      >
                        #{index + 1}
                      </div>
                      <div>
                        <Text style={{ 
                          fontWeight: 600,
                          color: theme === 'dark' ? '#ffffff' : '#262626'
                        }}>
                          {user.username}
                        </Text>
                        <div style={{ 
                          fontSize: '12px', 
                          color: theme === 'dark' ? '#bfbfbf' : '#8c8c8c' 
                        }}>
                          Ratio: {user.ratio.toFixed(2)} | Neto: ${user.netPosition.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '12px', color: '#52c41a' }}>
                        Depósitos: ${user.totalDeposits.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '12px', color: '#fa8c16' }}>
                        Retiros: ${user.totalWithdraws.toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Gráfico de Conversión: Usuarios Creados vs Depositados */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <div
            style={{
              background: theme === 'dark'
                ? '#23272f'
                : 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              padding: '20px',
              boxShadow: theme === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.32)'
                : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: theme === 'dark'
                ? '1px solid #23272f'
                : '1px solid rgba(0, 0, 0, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
            }}
          >
            {/* Gradiente decorativo superior */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '200px',
                height: '200px',
                background: theme === 'dark'
                  ? 'linear-gradient(135deg, #f5222d33, #23272f00)'
                  : 'linear-gradient(135deg, #f5222d20, #f5222d10)',
                borderRadius: '0 16px 0 200px',
                pointerEvents: 'none'
              }}
            />

            {/* Header del gráfico */}
            <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
              <Space align="center" size={12}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #f5222d, #ff7875)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '16px',
                    boxShadow: '0 4px 12px rgba(245, 34, 45, 0.3)'
                  }}
                >
                  <TeamOutlined />
                </div>
                <div>
                  <Text style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: theme === 'dark' ? '#ffffff' : '#262626',
                    margin: 0,
                    display: 'block'
                  }}>
                    Conversión de Usuarios
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Creados en rango vs Depositados
                  </Text>
                </div>
              </Space>
            </div>

            {/* Contenido del gráfico */}
            <Row gutter={[20, 20]} style={{ position: 'relative', zIndex: 1 }}>
              {/* Métricas principales - Cards compactos */}
              <Col xs={24}>
                <Row gutter={[12, 12]}>
                  {/* Total Creados */}
                  <Col xs={24} sm={8}>
                    <div style={{
                      background: theme === 'dark'
                        ? 'rgba(24, 144, 255, 0.1)'
                        : 'rgba(24, 144, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '16px',
                      border: theme === 'dark'
                        ? '1px solid rgba(24, 144, 255, 0.3)'
                        : '1px solid rgba(24, 144, 255, 0.2)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'rgba(24, 144, 255, 0.1)',
                        filter: 'blur(20px)'
                      }} />
                      <Space direction="vertical" size={4} style={{ width: '100%', position: 'relative' }}>
                        <Space size={6}>
                          <UserOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
                          <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                            Usuarios Creados
                          </Text>
                        </Space>
                        <Text strong style={{ fontSize: '32px', color: '#1890ff', display: 'block', lineHeight: '1' }}>
                          {(data?.usersCreatedInRange || 0).toLocaleString()}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          en el periodo seleccionado
                        </Text>
                      </Space>
                    </div>
                  </Col>

                  {/* Total Depositaron */}
                  <Col xs={24} sm={8}>
                    <div style={{
                      background: theme === 'dark'
                        ? 'rgba(82, 196, 26, 0.1)'
                        : 'rgba(82, 196, 26, 0.05)',
                      borderRadius: '12px',
                      padding: '16px',
                      border: theme === 'dark'
                        ? '1px solid rgba(82, 196, 26, 0.3)'
                        : '1px solid rgba(82, 196, 26, 0.2)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'rgba(82, 196, 26, 0.1)',
                        filter: 'blur(20px)'
                      }} />
                      <Space direction="vertical" size={4} style={{ width: '100%', position: 'relative' }}>
                        <Space size={6}>
                          <DollarOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
                          <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                            Hicieron Depósitos
                          </Text>
                        </Space>
                        <Text strong style={{ fontSize: '32px', color: '#52c41a', display: 'block', lineHeight: '1' }}>
                          {(data?.timeToFirstDeposit?.totalUsersWhoDeposited || 0).toLocaleString()}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          {(() => {
                            // Total de depósitos de usuarios creados en el rango
                            const totalDeposits = Math.round(
                              (data?.averageLoadsPerUserCreatedInRange || 0) * (data?.usersCreatedInRange || 0)
                            );
                            return `${totalDeposits.toLocaleString()} depósitos en total`;
                          })()}
                        </Text>
                      </Space>
                    </div>
                  </Col>

                  {/* Tasa de Conversión */}
                  <Col xs={24} sm={8}>
                    <div style={{
                      background: theme === 'dark'
                        ? 'rgba(250, 173, 20, 0.1)'
                        : 'rgba(250, 173, 20, 0.05)',
                      borderRadius: '12px',
                      padding: '16px',
                      border: theme === 'dark'
                        ? '1px solid rgba(250, 173, 20, 0.3)'
                        : '1px solid rgba(250, 173, 20, 0.2)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'rgba(250, 173, 20, 0.1)',
                        filter: 'blur(20px)'
                      }} />
                      <Space direction="vertical" size={4} style={{ width: '100%', position: 'relative' }}>
                        <Space size={6}>
                          <RiseOutlined style={{ color: '#faad14', fontSize: '14px' }} />
                          <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                            Tasa de Conversión
                          </Text>
                        </Space>
                        <Text strong style={{ fontSize: '32px', color: '#faad14', display: 'block', lineHeight: '1' }}>
                          {(data?.timeToFirstDeposit?.conversionRate || 0).toFixed(1)}%
                        </Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          de conversión efectiva
                        </Text>
                      </Space>
                    </div>
                  </Col>
                </Row>
              </Col>

              {/* Visualización comparativa - Barras profesionales */}
              <Col xs={24}>
                <div style={{
                  background: theme === 'dark' ? '#1f1f1f' : '#fafafa',
                  borderRadius: '12px',
                  padding: '24px',
                  border: theme === 'dark'
                    ? '1px solid #303030'
                    : '1px solid #e8e8e8'
                }}>
                  <Space direction="vertical" size={24} style={{ width: '100%' }}>
                    {/* Barra Usuarios Creados */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <Space size={8}>
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            background: 'linear-gradient(135deg, #1890ff, #096dd9)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.25)'
                          }}>
                            <UserOutlined style={{ color: 'white', fontSize: '12px' }} />
                          </div>
                          <div>
                            <Text style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: theme === 'dark' ? '#fff' : '#262626',
                              display: 'block'
                            }}>
                              Usuarios Creados
                            </Text>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              Base total del periodo
                            </Text>
                          </div>
                        </Space>
                        <div style={{ textAlign: 'right' }}>
                          <Text strong style={{ fontSize: '18px', color: '#1890ff', display: 'block' }}>
                            {(data?.usersCreatedInRange || 0).toLocaleString()}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            100%
                          </Text>
                        </div>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '48px',
                        background: theme === 'dark'
                          ? 'rgba(255, 255, 255, 0.03)'
                          : 'rgba(0, 0, 0, 0.03)',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        position: 'relative',
                        border: theme === 'dark'
                          ? '1px solid rgba(255, 255, 255, 0.06)'
                          : '1px solid rgba(0, 0, 0, 0.06)'
                      }}>
                        <div style={{
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, #1890ff 0%, #69c0ff 100%)',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: '16px',
                          boxShadow: '0 4px 12px rgba(24, 144, 255, 0.25)',
                          transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                          <Text strong style={{ fontSize: '14px', color: 'white' }}>
                            Base: {(data?.usersCreatedInRange || 0).toLocaleString()} usuarios
                          </Text>
                        </div>
                      </div>
                    </div>

                    {/* Barra Usuarios que Depositaron */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <Space size={8}>
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            background: 'linear-gradient(135deg, #52c41a, #389e0d)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.25)'
                          }}>
                            <DollarOutlined style={{ color: 'white', fontSize: '12px' }} />
                          </div>
                          <div>
                            <Text style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: theme === 'dark' ? '#fff' : '#262626',
                              display: 'block'
                            }}>
                              Usuarios que Depositaron
                            </Text>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              {(() => {
                                // Total de depósitos de usuarios creados en el rango
                                const totalDeposits = Math.round(
                                  (data?.averageLoadsPerUserCreatedInRange || 0) * (data?.usersCreatedInRange || 0)
                                );
                                return `${totalDeposits.toLocaleString()} depósitos realizados`;
                              })()}
                            </Text>
                          </div>
                        </Space>
                        <div style={{ textAlign: 'right' }}>
                          <Text strong style={{ fontSize: '18px', color: '#52c41a', display: 'block' }}>
                            {(data?.timeToFirstDeposit?.totalUsersWhoDeposited || 0).toLocaleString()}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            {(data?.timeToFirstDeposit?.conversionRate || 0).toFixed(1)}%
                          </Text>
                        </div>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '48px',
                        background: theme === 'dark'
                          ? 'rgba(255, 255, 255, 0.03)'
                          : 'rgba(0, 0, 0, 0.03)',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        position: 'relative',
                        border: theme === 'dark'
                          ? '1px solid rgba(255, 255, 255, 0.06)'
                          : '1px solid rgba(0, 0, 0, 0.06)'
                      }}>
                        <div style={{
                          width: `${data?.timeToFirstDeposit?.conversionRate || 0}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #52c41a 0%, #95de64 100%)',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: '16px',
                          boxShadow: '0 4px 12px rgba(82, 196, 26, 0.25)',
                          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                          minWidth: '120px'
                        }}>
                          <Text strong style={{ fontSize: '14px', color: 'white' }}>
                            {(data?.timeToFirstDeposit?.conversionRate || 0).toFixed(1)}%
                          </Text>
                        </div>
                      </div>
                    </div>

                    {/* Barra No Depositaron */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <Space size={8}>
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            background: 'linear-gradient(135deg, #ff4d4f, #cf1322)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(255, 77, 79, 0.25)'
                          }}>
                            <CloseOutlined style={{ color: 'white', fontSize: '12px' }} />
                          </div>
                          <div>
                            <Text style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: theme === 'dark' ? '#fff' : '#262626',
                              display: 'block'
                            }}>
                              No Depositaron
                            </Text>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              Usuarios sin conversión
                            </Text>
                          </div>
                        </Space>
                        <div style={{ textAlign: 'right' }}>
                          <Text strong style={{ fontSize: '18px', color: '#ff4d4f', display: 'block' }}>
                            {((data?.usersCreatedInRange || 0) - (data?.timeToFirstDeposit?.totalUsersWhoDeposited || 0)).toLocaleString()}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            {(100 - (data?.timeToFirstDeposit?.conversionRate || 0)).toFixed(1)}%
                          </Text>
                        </div>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '48px',
                        background: theme === 'dark'
                          ? 'rgba(255, 255, 255, 0.03)'
                          : 'rgba(0, 0, 0, 0.03)',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        position: 'relative',
                        border: theme === 'dark'
                          ? '1px solid rgba(255, 255, 255, 0.06)'
                          : '1px solid rgba(0, 0, 0, 0.06)'
                      }}>
                        <div style={{
                          width: `${100 - (data?.timeToFirstDeposit?.conversionRate || 0)}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #ff4d4f 0%, #ff7875 100%)',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: '16px',
                          boxShadow: '0 4px 12px rgba(255, 77, 79, 0.25)',
                          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                          minWidth: '120px'
                        }}>
                          <Text strong style={{ fontSize: '14px', color: 'white' }}>
                            {(100 - (data?.timeToFirstDeposit?.conversionRate || 0)).toFixed(1)}%
                          </Text>
                        </div>
                      </div>
                    </div>
                  </Space>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      {/* Gráficos principales */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <div
            style={{
              background: theme === 'dark'
                ? '#23272f'
                : 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              padding: '20px',
              boxShadow: theme === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.32)'
                : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: theme === 'dark'
                ? '1px solid #23272f'
                : '1px solid rgba(0, 0, 0, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
            }}
          >
            {/* Header del gráfico */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1890ff, #096dd9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '14px'
                }}
              >
                <RiseOutlined />
              </div>
              <Text style={{
                fontSize: '16px',
                fontWeight: 600,
                color: theme === 'dark' ? '#ffffff' : '#262626',
                margin: 0
              }}>
                {t("analytics.userAnalytics.charts.userGrowthTrend.title")}
              </Text>
            </div>
            <TrendLineChart
              data={
                data?.trends
                  .filter(
                    (item) =>
                      !filters.accountName ||
                      item.accountName === filters.accountName
                  )
                  .map((item) => ({
                    date: item.date,
                    value: item.count,
                    type: item.accountName,
                  })) || []
              }
              xField="date"
              yField="value"
              seriesField="type"
              loading={loading}
            />
          </div>
        </Col>

        <Col xs={24} lg={12}>
          <div
            style={{
              background: theme === 'dark' 
                ? '#23272f' 
                : 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              padding: '20px',
              boxShadow: theme === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.32)'
                : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: theme === 'dark'
                ? '1px solid #23272f'
                : '1px solid rgba(0, 0, 0, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
            }}
          >
            {/* Header del gráfico */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #52c41a, #389e0d)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '14px'
                }}
              >
                <CrownOutlined />
              </div>
              <Text style={{ 
                fontSize: '16px', 
                fontWeight: 600, 
                color: theme === 'dark' ? '#ffffff' : '#262626', 
                margin: 0 
              }}>
                {t("analytics.userAnalytics.charts.topLoaders.title")}
              </Text>
            </div>
            <TopTierChart
              data={data?.topLoaders || []}
              nameField="username"
              valueField="count"
              metricField="amount"
              metricPrefix="$"
              loading={loading}
            />
          </div>
        </Col>
      </Row>

      {/* Gráficos secundarios */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <div
            style={{
              background: theme === 'dark' 
                ? '#23272f' 
                : 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              padding: '20px',
              boxShadow: theme === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.32)'
                : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: theme === 'dark'
                ? '1px solid #23272f'
                : '1px solid rgba(0, 0, 0, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
            }}
          >
            {/* Header del gráfico */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #fa8c16, #d46b08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '14px'
                }}
              >
                <TrophyOutlined />
              </div>
              <Text style={{ 
                fontSize: '16px', 
                fontWeight: 600, 
                color: theme === 'dark' ? '#ffffff' : '#262626', 
                margin: 0 
              }}>
                {t("analytics.userAnalytics.charts.topWithdrawers.title")}
              </Text>
            </div>
            <TopTierChart
              data={data?.topWithdrawers || []}
              nameField="username"
              valueField="count"
              metricField="amount"
              metricPrefix="$"
              loading={loading}
            />
          </div>
        </Col>

        <Col xs={24} lg={12}>
          <div
            style={{
              background: theme === 'dark' 
                ? '#23272f' 
                : 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              padding: '20px',
              boxShadow: theme === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.32)'
                : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: theme === 'dark'
                ? '1px solid #23272f'
                : '1px solid rgba(0, 0, 0, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
            }}
          >
            {/* Header del gráfico */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #722ed1, #531dab)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '14px'
                }}
              >
                <DollarOutlined />
              </div>
              <Text style={{ 
                fontSize: '16px', 
                fontWeight: 600, 
                color: theme === 'dark' ? '#ffffff' : '#262626', 
                margin: 0 
              }}>
                {t("analytics.userAnalytics.charts.topLoadAmounts.title")}
              </Text>
            </div>
            <TopTierChart
              data={data?.topLoadAmounts || []}
              nameField="username"
              valueField="amount"
              metricPrefix="$"
              loading={loading}
            />
          </div>
        </Col>
      </Row>

      {/* Gráfico de depósitos */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <div
            style={{
              background: theme === 'dark' 
                ? '#23272f' 
                : 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              padding: '20px',
              boxShadow: theme === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.32)'
                : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: theme === 'dark'
                ? '1px solid #23272f'
                : '1px solid rgba(0, 0, 0, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
            }}
          >
            {/* Header del gráfico */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #13c2c2, #08979c)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '14px'
                }}
              >
                <TeamOutlined />
              </div>
              <Text style={{ 
                fontSize: '16px', 
                fontWeight: 600, 
                color: theme === 'dark' ? '#ffffff' : '#262626', 
                margin: 0 
              }}>
                {t("analytics.userAnalytics.charts.userDepositDetails.title") || "Usuarios con Más Depósitos"}
              </Text>
            </div>
            <TopTierChart
              data={data?.userDepositDetails || []}
              nameField="username"
              valueField="depositCount"
              metricField="totalAmount"
              metricPrefix="$"
              loading={loading}
            />
          </div>
        </Col>
      </Row>

      {/* Tabla de Usuarios */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <div
            style={{
              background: theme === 'dark' 
                ? '#23272f' 
                : 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              padding: '20px',
              boxShadow: theme === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.32)'
                : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: theme === 'dark'
                ? '1px solid #23272f'
                : '1px solid rgba(0, 0, 0, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
            }}
          >
          </div>
        </Col>
      </Row>

      {/* Drawer de detalles de usuarios */}
      <UserDetailsDrawer
        visible={drawerState.visible}
        onClose={handleCloseDrawer}
        category={drawerState.category}
        categoryData={drawerState.categoryData}
        onLoadDetails={handleLoadUserDetails}
        theme={theme}
      />

      {/* Drawer para Usuarios Super Activos / Que Regresan */}
      <StreakUsersDrawer
        visible={streakDrawerState.visible}
        onClose={handleCloseStreakDrawer}
        type={streakDrawerState.type}
        users={
          streakDrawerState.type === 'superActive'
            ? (data?.streakAnalysis?.superActiveUsers?.details || [])
            : (data?.streakAnalysis?.returningUsers?.details || [])
        }
        threshold={
          streakDrawerState.type === 'superActive'
            ? (customThresholds.superActive || data?.streakAnalysis?.superActiveUsers?.threshold || 0)
            : (customThresholds.returning || data?.streakAnalysis?.returningUsers?.inactivityDays || 0)
        }
        theme={theme}
      />

      {/* Modal para configurar thresholds */}
      <Modal
        title={
          configType === 'superActive'
            ? '⚙️ Configurar Umbral de Super Activos'
            : '⚙️ Configurar Días de Inactividad'
        }
        open={configModalVisible}
        onCancel={() => {
          setConfigModalVisible(false);
        }}
        onOk={handleApplyThreshold}
        okText="Aplicar"
        cancelText="Cancelar"
        width={500}
        zIndex={1000}
        destroyOnClose
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {configType === 'superActive'
                ? 'Número mínimo de cargas para considerar un usuario como super activo:'
                : 'Número mínimo de días de inactividad para considerar que un usuario "regresó":'}
            </Text>
            <InputNumber
              min={1}
              max={configType === 'superActive' ? 500 : 90}
              value={tempThreshold}
              onChange={(value) => setTempThreshold(value || 0)}
              style={{ width: '100%' }}
              size="large"
              suffix={configType === 'superActive' ? 'cargas' : 'días'}
            />
          </div>

          <div style={{
            background: theme === 'dark' ? '#23272f' : '#f0f2f5',
            padding: '12px 16px',
            borderRadius: '8px'
          }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              💡 <strong>Nota:</strong> Al cambiar este valor, se recalcularán las estadísticas automáticamente.
              El valor por defecto se calcula en base al período de tiempo filtrado.
            </Text>
          </div>

          <Button
            type="link"
            danger
            onClick={handleResetThreshold}
            style={{ padding: 0 }}
          >
            🔄 Restaurar valor automático
          </Button>
        </Space>
      </Modal>
    </div>
  );
};

export default UserAnalytics;